import React from 'react';
import { ModalInfo } from 'components';
import {
  Text,
  ModalHeader,
  ModalBody,
  Grid,
  IconButton,
  Box,
} from '@chakra-ui/react';
import { ProductParams } from 'store/products';
import { Fieldset } from 'components';
import {
  SingleField,
  MultiOptionField,
  FileField,
} from 'components/ModalInfo.Fields';
import {
  ProductCategoryInfo,
  ProductAdSlotsInfo,
  ProductAdFormatInfo,
  ProductSegmentInfo,
} from 'components/Product/infoFields';
import { Link } from 'react-router-dom';
import { FieldsetSkeleton } from 'components/Skeletons';
import { useTranslation } from 'react-i18next';
import { useGetProposalById } from 'queries/proposals';
import { useGetProductById } from 'queries/products';
import { InfoIcon } from '@chakra-ui/icons';

export const ShowProductModalInfo = ({
  text,
  product,
  linkStyle,
}: {
  text: string;
  product: ProductParams;
  linkStyle?: any;
}): JSX.Element => {
  return (
    <ModalInfo
      trigger={
        <Link to='#'>
          <Text variant='link' as='span' {...linkStyle}>
            {text}
          </Text>
        </Link>
      }
      info={<ProductModalInfo {...product} />}
    />
  );
};

export const ShowProductSnapshotModalInfo = ({
  proposalId,
  productName,
  ...props
}: {
  proposalId: number;
  productName: string;
  [key: string]: unknown;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <>
      <Box as='span' mr='1'>
        {productName}
      </Box>
      <ModalInfo
        trigger={
          <IconButton
            aria-label={t('product_settings', 'Ustawienia produktu')}
            icon={<InfoIcon />}
            variant='unstyled'
            color='nexusTeal.100'
            size='xs'
            verticalAlign='middle'
            {...props}
          />
        }
        info={<ProductSnapshotModalInfo proposalId={proposalId} />}
      />
    </>
  );
};

export const ProductModalDisplay = ({
  product,
}: {
  product: ProductParams;
}): JSX.Element => {
  const { t } = useTranslation();
  const { generalValue, timedSegments } = product || {};

  return (
    <>
      <ModalHeader mb={4}>{t('productInfo', 'Product info')}</ModalHeader>
      <ModalBody>
        <Grid gap={6} templateColumns='repeat(3,1fr)'>
          <Fieldset gridColumn='span 3'>
            <SingleField
              header={t('product.name', 'Nazwa produktu')}
              value={generalValue.name}
              gridColumn={{ base: 'span 9', md: 'span 9' }}
            />
            <SingleField
              header={t('product.pricingBasis', 'Rodzaj stawki')}
              value={generalValue.pricingBasis}
              gridColumn={{ base: 'span 3', md: 'span 3' }}
            />
          </Fieldset>
          {timedSegments.map((segment, index) => (
            <ProductSegmentInfo
              key={index}
              value={segment}
              gridColumn={{ base: 'span 12', md: 'span 12' }}
            />
          ))}
          <Fieldset gridColumn='span 3'>
            <SingleField
              header={t('product.adType', 'Rodzaj emisji')}
              value={generalValue.adType}
              gridColumn={{ base: 'span 4', md: 'span 4' }}
            />
            <SingleField
              header={t('product.pricingModel', 'Model rozliczeniowy')}
              value={generalValue.pricingModel}
              gridColumn={{ base: 'span 4', md: 'span 4' }}
            />
            <MultiOptionField
              header={t('product.emissionModels', 'Miejsce emisji')}
              elements={generalValue.emissionModels}
              gridColumn={{ base: 'span 4', md: 'span 4' }}
            />
          </Fieldset>
          <Fieldset gridColumn='span 3'>
            <SingleField
              header={t('product.services', 'Serwisy/Aplikacje')}
              value={generalValue.services}
              gridColumn={{ base: 'span 12', md: 'span 12' }}
            />
          </Fieldset>
          <Fieldset gridColumn='span 3'>
            <ProductCategoryInfo
              value={generalValue.categoryIds}
              gridColumn={{ base: 'span 12', md: 'span 12' }}
            />
          </Fieldset>
          <Fieldset gridColumn='span 3'>
            <ProductAdSlotsInfo
              value={generalValue.adSlotIds}
              gridColumn={{ base: 'span 4', md: 'span 4' }}
              mode='overflow'
            />
            <ProductAdFormatInfo
              value={generalValue.adFormatIds}
              gridColumn={{ base: 'span 4', md: 'span 4' }}
              mode='overflow'
            />
            <SingleField
              header={t('product.emissionPlatform', 'Platforma emisji')}
              value={generalValue.emissionPlatform}
              gridColumn={{ base: 'span 4', md: 'span 4' }}
            />
          </Fieldset>
          <Fieldset gridColumn='span 3'>
            <FileField
              header={t('product.visualSpec.label', 'Wizualizacja')}
              value={{
                files: generalValue.visualSpec?.files ?? [],
                providedUrls: generalValue.visualSpec?.providedUrls ?? [],
              }}
              gridColumn={{ base: 'span 6', md: 'span 6' }}
            />
            <FileField
              header={t(
                'product.technicalSpec.label',
                'Specyfikacja techniczna',
              )}
              value={{
                files: generalValue.technicalSpec?.files ?? [],
                providedUrls: generalValue.technicalSpec?.providedUrls ?? [],
              }}
              gridColumn={{ base: 'span 6', md: 'span 6' }}
            />
          </Fieldset>
          <Fieldset gridColumn='span 3'>
            <SingleField
              header={t('product.commentary', 'Komentarz')}
              value={generalValue.commentary}
              gridColumn={{ base: 'span 12', md: 'span 12' }}
            />
          </Fieldset>
        </Grid>
      </ModalBody>
    </>
  );
};

export const ProductModalInfo = (product: ProductParams): JSX.Element => {
  const { data: productValues } = useGetProductById(product.id);
  if (!productValues?.timedSegments) return <FieldsetSkeleton />;

  return <ProductModalDisplay product={productValues} />;
};

export const ProductSnapshotModalInfo = ({
  proposalId,
}: {
  proposalId: number;
}): JSX.Element => {
  const { data: proposalValues } = useGetProposalById(proposalId);

  const productSnapshot = proposalValues?.productSnapshot;

  if (!productSnapshot) return <>-</>;

  return <ProductModalDisplay product={productSnapshot} />;
};
